/* @sravni/react-design-system version 0.2.60 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useStyles, useMergedRef, useBoolean, preventBodyScroll, safeInvoke } from '@sravni/react-utils';
import { Cross } from '@sravni/react-icons';
import { useBackwardEvent } from '../../../hooks/useBackwardEvent.js';
import '../../Portal/index.js';
import '../../Icon/index.js';
import '../../Transition/index.js';
import SheetContext from './SheetContext.js';
import modules_0b64ef5e from '../styles/Sheet.module.less.js';
import { EASINGS } from '../../Transition/src/_utils.js';
import Portal from '../../Portal/src/Portal.js';
import Icon from '../../Icon/src/Icon.js';

var defaultDragElastic = { top: 0.5, bottom: 0.5 };
/**
 * @description Sheet component.
 *
 * @component
 * @example
 * ```jsx
 * <Sheet>content</Sheet>
 * ```
 */
var Sheet = React.forwardRef(function (props, ref) {
    var visible = props.visible, closable = props.closable, fullscreen = props.fullscreen, _a = props.closeOnBackward, closeOnBackward = _a === void 0 ? false : _a, className = props.className, children = props.children, onClose = props.onClose, restProps = __rest(props, ["visible", "closable", "fullscreen", "closeOnBackward", "className", "children", "onClose"]);
    var cx = useStyles(modules_0b64ef5e);
    var sheetRef = React.useRef(null);
    var isUnmountEvent = React.useRef(true);
    var referenceRef = useMergedRef(sheetRef, ref);
    var _b = __read(React.useState(defaultDragElastic), 2), dragElastic = _b[0], setDragElastic = _b[1];
    // Auxiliary transparent layer helps to disable user interaction while animation is in progress
    var _c = __read(useBoolean(false), 2), isLayerEnabled = _c[0], setIsLayerEnabled = _c[1];
    var handleClose = React.useCallback(function (event) {
        preventBodyScroll();
        safeInvoke(onClose, event);
        isUnmountEvent.current = false;
    }, [onClose]);
    var handleDragEnd = React.useCallback(function (event, _a) {
        var velocity = _a.velocity;
        var sheetEl = sheetRef.current;
        if (!sheetEl)
            return;
        var contentHeight = sheetEl.getBoundingClientRect().height;
        // Close if dragged over 60%
        if (velocity.y / contentHeight > 0.6) {
            handleClose(event);
        }
    }, [sheetRef, handleClose]);
    var handlePreventBodyScroll = React.useCallback(function () {
        if (visible && isUnmountEvent.current) {
            preventBodyScroll();
        }
    }, [visible, isUnmountEvent]);
    var sheetMotionProps = React.useMemo(function () { return ({
        initial: 'exit',
        exit: 'exit',
        variants: {
            enter: { y: 0, transition: { duration: 0.2, type: 'easeOut' } },
            exit: {
                y: sheetRef.current ? sheetRef.current.getBoundingClientRect().height : 1000,
                transition: { duration: 0.2, type: 'easeOut' },
            },
        },
        animate: visible ? 'enter' : 'exit',
    }); }, [visible, sheetRef]);
    var backdropMotionProps = React.useMemo(function () { return ({
        variants: {
            exit: {
                opacity: 0,
                transition: {
                    duration: 0.2,
                    delay: 0.2,
                    ease: EASINGS.easeOut,
                },
            },
            enter: {
                opacity: 0.6,
                transition: {
                    duration: 0.2,
                    delay: 0.1,
                    ease: EASINGS.easeIn,
                },
            },
        },
        animate: visible ? 'enter' : 'exit',
        initial: 'exit',
        exit: 'exit',
    }); }, [visible]);
    var dragProps = React.useMemo(function () { return ({
        drag: 'y',
        dragElastic: dragElastic,
        dragConstraints: { top: 0, bottom: 0 },
        dragMomentum: false,
        onDragEnd: handleDragEnd,
    }); }, [dragElastic, handleDragEnd]);
    var handlePointerDown = React.useCallback(function () { return setDragElastic(defaultDragElastic); }, []);
    React.useEffect(function () {
        if (visible) {
            preventBodyScroll(true);
            isUnmountEvent.current = true;
        }
        return handlePreventBodyScroll;
    }, [visible]);
    var closeEventHandler = useBackwardEvent(closeOnBackward, !!visible, handleClose).closeEventHandler;
    return (React.createElement(Portal, null,
        isLayerEnabled && React.createElement("div", { className: cx('auxiliaryLayer') }),
        React.createElement(AnimatePresence, { onExitComplete: closeEventHandler }, visible && (React.createElement("div", { className: cx('overlay') },
            React.createElement(motion.div, __assign({}, backdropMotionProps, { className: cx('backdrop'), onClick: handleClose })),
            React.createElement(motion.div, __assign({ "data-qa": "Sheet" }, dragProps, sheetMotionProps, restProps, { ref: referenceRef, className: cx(className, 'container', { fullscreen: fullscreen }), onPointerDown: handlePointerDown, onAnimationStart: setIsLayerEnabled.on, onAnimationComplete: setIsLayerEnabled.off }),
                closable && React.createElement(Icon, { className: cx('close'), icon: React.createElement(Cross, null), onClick: handleClose }),
                React.createElement(SheetContext.Provider, { value: { setDragElastic: setDragElastic } }, children)))))));
});
Sheet.defaultProps = {
    closeOnBackward: false,
};
Sheet.displayName = 'Sheet';

export default Sheet;
//# sourceMappingURL=Sheet.js.map
